// Lubbock 3D Printing — new quote builder.
// Loaded by sections/l3d-quote-builder.liquid. Talks to the quote server through the
// Shopify app proxy (/apps/l3d/api), which adds logged_in_customer_id.
//
// Flow: pick Single color or Multicolor → add parts → choose material / colors → delivery
// → submit (upload files, POST /quotes) → live slicing results and website-computed price.

const root = document.querySelector('[data-l3d-qb]');
if (root) boot(root);

async function boot(root) {
  const cfgEl = root.querySelector('script[data-l3d-qb-config]');
  const CFG = JSON.parse(cfgEl ? cfgEl.textContent : '{}');
  const [P, M3, V] = await Promise.all([
    import(CFG.modules.pricing),
    import(CFG.modules.parser),
    import(CFG.modules.viewer),
  ]);
  new QuoteBuilder(root, CFG, P, M3, V).start();
}

/* ---------------- small helpers ---------------- */

const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const uid = () => Math.random().toString(36).slice(2, 10);
const extOf = (name) => (String(name).split('.').pop() || '').toLowerCase();
const mb = (bytes) => bytes < 1048576 ? Math.max(1, Math.round(bytes / 1024)) + ' KB' : (bytes / 1048576).toFixed(bytes > 10485760 ? 0 : 1) + ' MB';
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function hexRgb(hex) {
  const n = parseInt(String(hex || '#000').replace('#', '').slice(0, 6), 16) || 0;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
// Perceptual distance (CIE76 in Lab) so "blue in the file" maps to our blue, not our gray.
function toLab(hex) {
  const lin = (c) => { c /= 255; return c > 0.04045 ? ((c + 0.055) / 1.055) ** 2.4 : c / 12.92; };
  const [r, g, b] = hexRgb(hex).map(lin);
  const f = (t) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  const x = f((r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047);
  const y = f(r * 0.2126 + g * 0.7152 + b * 0.0722);
  const z = f((r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883);
  return [116 * y - 16, 500 * (x - y), 200 * (y - z)];
}
function colorDist(a, b) {
  const [l1, a1, b1] = toLab(a), [l2, a2, b2] = toLab(b);
  return Math.hypot(l1 - l2, (a1 - a2) * 1.3, (b1 - b2) * 1.3);
}
function inkOn(hex) {
  const [r, g, b] = hexRgb(hex);
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? '#1b1b1b' : '#ffffff';
}

/* ---------------- builder ---------------- */

class QuoteBuilder {
  constructor(root, cfg, pricing, parser, viewer) {
    this.root = root;
    this.cfg = cfg;
    this.P = pricing;
    this.M3 = parser;
    this.V = viewer;
    this.api = cfg.apiBase || '/apps/l3d/api';
    this.palette = (cfg.colors || []).filter((c) => c.inStock !== false && c.hex);
    this.state = {
      phase: 'loading', // loading | login | error | type | build | submitting | done
      mode: null,       // 'single' | 'multi'
      parts: [],
      activeId: null,
      delivery: null,
      address: {},
      notes: '',
      agreed: false,          // Terms of Service checkbox; the server requires it
      addressNotFound: false, // the address lookup couldn't find the address
      addressAsTyped: false,  // ...and the customer confirmed it is correct
      submitError: '',
      quoteId: null,
      quote: null,
    };
    this.me = null;
    this.viewer = null;
    this.viewerModelId = null;
  }

  /* ---------- server ---------- */

  async call(path, opts = {}) {
    const res = await fetch(this.api + path, {
      method: opts.method || 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(opts.body ? { 'Content-Type': 'application/json' } : {}) },
      body: opts.body ? JSON.stringify(opts.body) : undefined,
    });
    let data = null;
    try { data = await res.json(); } catch { /* empty */ }
    if (!res.ok) {
      const err = new Error((data && (data.error || data.message)) || `Request failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    return data;
  }

  get options() {
    const o = (this.me && this.me.options) || {};
    return {
      materials: o.materials || ['PLA', 'PETG', 'ABS', 'TPU'],
      multicolor: { material: 'PLA', maxSlots: 4, ...(o.multicolor || {}) },
      fileTypes: (o.fileTypes || ['stl', '3mf']).map((t) => String(t).toLowerCase()),
      deliveries: o.deliveries || { pickup: { title: 'Local meetup' } },
      advanced: o.advanced || {},
      maxUploadMb: o.maxUploadMb || 300,
      states: o.states || [],
      localRadius: o.localRadius || 20,
    };
  }

  async start() {
    this.root.addEventListener('click', (e) => this.onClick(e));
    this.root.addEventListener('change', (e) => this.onChange(e));
    this.root.addEventListener('input', (e) => this.onInput(e));
    this.render();
    try {
      this.me = await this.call('/me');
      if (!this.me || !this.me.customer) { this.state.phase = 'login'; return this.render(); }
      const addr = this.me.address || {};
      this.state.address = {
        name: addr.name || this.me.customer.name || '',
        address1: addr.address1 || addr.line1 || addr.street || '',
        address2: addr.address2 || addr.line2 || '',
        city: addr.city || '',
        state: addr.state || addr.province || addr.provinceCode || 'TX',
        zip: addr.zip || addr.postal || addr.postalCode || '',
      };
      const deliveries = Object.keys(this.options.deliveries);
      this.state.delivery = deliveries.includes('pickup') ? 'pickup' : deliveries[0];
      this.state.phase = 'type';
    } catch (err) {
      this.state.phase = err.status === 401 || err.status === 403 ? 'login' : 'error';
      this.state.submitError = err.message;
    }
    this.render();
  }

  /* ---------- palette ---------- */

  colorsFor(material) {
    const m = String(material || 'PLA').toUpperCase();
    return this.palette.filter((c) => String(c.material || 'PLA').toUpperCase() === m);
  }

  defaultSlotColors(part) {
    const pla = this.colorsFor('PLA');
    const picks = {};
    const used = new Set();
    for (const slot of part.slots) {
      const fileHex = part.model && part.model.fileColors && part.model.fileColors[slot];
      let pick = null;
      if (fileHex && pla.length) {
        pick = [...pla].sort((a, b) => colorDist(a.hex, fileHex) - colorDist(b.hex, fileHex)).find((c) => !used.has(c.name)) || null;
      }
      if (!pick) pick = pla.find((c) => !used.has(c.name)) || pla[(slot - 1) % Math.max(1, pla.length)] || null;
      if (pick) { picks[slot] = pick; used.add(pick.name); }
    }
    return picks;
  }

  /* ---------- parts ---------- */

  acceptFor(mode) {
    const types = this.options.fileTypes;
    return mode === 'multi' ? types.filter((t) => t === '3mf') : types;
  }

  async addFiles(fileList) {
    const files = [...fileList];
    const accept = this.acceptFor(this.state.mode);
    for (const file of files) {
      const ext = extOf(file.name);
      const part = {
        id: uid(), file, name: file.name, ext, size: file.size,
        qty: 1, material: this.state.mode === 'multi' ? 'PLA' : this.options.materials[0] || 'PLA',
        color: null, supportMaterial: '', units: 'mm', notes: '', settings: {}, advancedOpen: false,
        model: null, parsing: false, parseError: '', error: '', slots: [], colors: {}, upload: null,
      };
      this.checkPart(part);
      const singleColors = this.colorsFor(part.material);
      if (this.state.mode === 'single') part.color = singleColors[0] || null;
      this.state.parts.push(part);
      if (!this.state.activeId || !this.active) this.state.activeId = part.id;
      this.render();
      if (!part.error) await this.parsePart(part);
    }
  }

  async parsePart(part) {
    if (part.size > 150 * 1048576) {
      part.parseError = 'Too large to preview here, but we can still slice it.';
      return this.render();
    }
    part.parsing = true;
    this.render();
    await sleep(20);
    try {
      let model = await this.M3.parseModelFile(part.file);
      part.rawModel = model;
      if (part.ext === 'stl' && part.units === 'in') model = this.M3.scaleModel(model, 25.4);
      part.model = model;
      part.slots = model.slots;
      this.checkPart(part);
      if (this.state.mode === 'multi') part.colors = this.defaultSlotColors(part);
    } catch (err) {
      part.parseError = (err && err.message) || 'Could not read this file for the preview.';
      if (this.state.mode === 'multi') part.parseError += ' You can still submit it and pick colors after it slices.';
    }
    part.parsing = false;
    this.render();
  }

  // Sets part.error (blocks submit) and part.note (informational) for the current print type.
  checkPart(part) {
    const multi = this.state.mode === 'multi';
    const accept = this.acceptFor(this.state.mode);
    part.error = '';
    part.note = '';
    if (!accept.includes(part.ext)) {
      part.error = multi
        ? 'Multicolor prints need a 3MF file with your colors painted or assigned (Bambu Studio, OrcaSlicer or PrusaSlicer).'
        : `Only ${accept.map((t) => t.toUpperCase()).join(' and ')} files are accepted.`;
      return;
    }
    if (part.size > this.options.maxUploadMb * 1048576) {
      part.error = `This file is ${mb(part.size)}. The limit is ${this.options.maxUploadMb} MB.`;
      return;
    }
    if (!part.slots.length) return;
    if (multi) {
      const max = this.options.multicolor.maxSlots;
      const highest = Math.max(...part.slots);
      if (part.slots.length > max) {
        part.error = `This file uses ${part.slots.length} colors. We can print up to ${max} colors in one part.`;
      } else if (highest > max) {
        part.error = `This file uses filament slot ${highest}. Reassign its colors to slots 1–${max} in your slicer and upload it again.`;
      } else if (part.slots.length === 1) {
        part.note = 'Only one color is assigned in this file, so it prints like a single-color part.';
      }
    } else if (part.slots.length > 1) {
      part.note = `This file has ${part.slots.length} colors assigned. As a single-color print it all comes out in one color — switch to Multicolor to keep them.`;
    }
  }

  get active() {
    return this.state.parts.find((p) => p.id === this.state.activeId) || null;
  }

  removePart(id) {
    this.state.parts = this.state.parts.filter((p) => p.id !== id);
    if (this.state.activeId === id) this.state.activeId = this.state.parts[0] ? this.state.parts[0].id : null;
    this.render();
  }

  setMode(mode) {
    const s = this.state;
    if (s.mode === mode) { s.phase = 'build'; return this.render(); }
    if (s.mode && s.parts.length) {
      const drop = mode === 'multi' ? s.parts.filter((p) => p.ext !== '3mf') : [];
      const msg = drop.length
        ? `Switching to multicolor removes ${drop.length} STL part${drop.length > 1 ? 's' : ''}. Continue?`
        : 'Switch print type? Your parts stay, but material and color choices reset.';
      if (!window.confirm(msg)) return;
      s.parts = s.parts.filter((p) => !drop.includes(p));
    }
    s.mode = mode;
    for (const p of s.parts) {
      this.checkPart(p);
      if (mode === 'multi') {
        p.material = 'PLA'; p.supportMaterial = ''; p.color = null;
        p.colors = this.defaultSlotColors(p);
      } else {
        p.colors = {};
        p.color = this.colorsFor(p.material)[0] || null;
      }
    }
    if (!s.parts.find((p) => p.id === s.activeId)) s.activeId = s.parts[0] ? s.parts[0].id : null;
    s.phase = 'build';
    this.render();
  }

  /* ---------- validation ---------- */

  needsAddress() {
    return this.state.delivery === 'local' || this.state.delivery === 'ship';
  }

  problems() {
    const s = this.state, out = [];
    if (!s.parts.length) out.push('Add at least one part.');
    for (const p of s.parts) {
      if (p.error) out.push(`${p.name}: ${p.error}`);
      if (p.parsing) out.push(`${p.name}: still reading the file…`);
      if (s.mode === 'multi' && p.model && p.slots.some((slot) => !p.colors[slot])) out.push(`${p.name}: choose a color for every slot.`);
    }
    if (!s.delivery) out.push('Choose a delivery option.');
    if (s.addressNotFound && !s.addressAsTyped) out.push('We couldn’t find that address. Fix it, or tick “use it as typed”.');
    if (!s.agreed) out.push('Confirm you have the right to print these files and agree to the Terms of Service.');
    if (this.needsAddress()) {
      const a = s.address;
      if (!a.address1 || !a.city || !a.state || !a.zip) out.push('Enter your delivery address.');
    }
    return out;
  }

  /* ---------- submit ---------- */

  uploadFile(part, token) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(token.method || 'PUT', token.uploadUrl);
      const headers = token.headers || {};
      if (!Object.keys(headers).some((h) => h.toLowerCase() === 'content-type')) {
        xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      }
      for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) { part.upload.progress = e.loaded / e.total; this.renderProgress(); }
      };
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
      xhr.onerror = () => reject(new Error('Upload failed. Check your connection and try again.'));
      xhr.send(part.file);
    });
  }

  buildItem(part, uploadId) {
    const multi = this.state.mode === 'multi';
    const settings = {
      ...part.settings,
      quoteType: multi ? 'multicolor' : 'single',
      rateCard: this.P.rateCardSnapshot(),
    };
    if (!multi && part.supportMaterial) settings.supportMaterial = part.supportMaterial;
    const item = {
      name: part.name,
      uploadId,
      qty: part.qty,
      material: multi ? 'PLA' : part.material,
      color: multi ? '' : part.color ? part.color.name : 'Your pick',
      units: part.ext === 'stl' ? part.units : 'mm',
      mode: Object.keys(part.settings).length ? 'advanced' : 'simple',
      settings,
      notes: part.notes,
    };
    if (multi) item.colors = this.colorsPayload(part);
    return item;
  }

  colorsPayload(part) {
    return Object.entries(part.colors)
      .map(([slot, c]) => ({ slot: Number(slot), hex: c.hex, name: c.name }))
      .sort((a, b) => a.slot - b.slot);
  }

  async submit() {
    const s = this.state;
    if (this.problems().length) return this.render();
    s.phase = 'submitting';
    s.submitError = '';
    for (const p of s.parts) p.upload = { progress: 0, done: false };
    this.render();
    try {
      const items = [];
      for (const part of s.parts) {
        if (!part.upload.uploadId) {
          const token = await this.call('/upload-token', {
            method: 'POST',
            body: { name: part.name, size: part.size, ext: part.ext, type: part.file.type || 'application/octet-stream' },
          });
          await this.uploadFile(part, token);
          part.upload.uploadId = token.uploadId || token.id;
        }
        part.upload.done = true;
        part.upload.progress = 1;
        this.renderProgress();
        items.push(this.buildItem(part, part.upload.uploadId));
      }
      const body = { items, delivery: s.delivery, notes: s.notes, agreed: s.agreed };
      if (this.needsAddress()) { body.address = { ...s.address }; body.addressAsTyped = s.addressAsTyped; }
      const res = await this.call('/quotes', { method: 'POST', body });
      const q = (res && res.quote) || res || {};
      s.quoteId = q.quoteId || q.id || res.quoteId;
      if (!s.quoteId) throw new Error('The server did not return a quote number.');
      if (q.items) s.quote = q;
      s.phase = 'done';
      this.render();
      try { history.replaceState(null, '', `${location.pathname}?q=${encodeURIComponent(s.quoteId)}`); } catch { /* ignore */ }
      this.poll();
    } catch (err) {
      s.phase = 'build';
      s.submitError = err.message || 'Something went wrong. Please try again.';
      // The files are already uploaded; fixing the address (or confirming it) and submitting again reuses them.
      if (/couldn.t find that address/i.test(s.submitError)) s.addressNotFound = true;
      this.render();
    }
  }

  async poll() {
    const s = this.state;
    const started = Date.now();
    let delay = 3000;
    while (s.phase === 'done' && Date.now() - started < 30 * 60 * 1000) {
      try {
        const res = await this.call(`/quotes/${encodeURIComponent(s.quoteId)}`);
        s.quote = (res && res.quote) || res;
        await this.syncColors();
        this.render();
        const items = (s.quote && s.quote.items) || [];
        if (items.length && items.every((it) => it.slice && (it.slice.status === 'done' || it.slice.status === 'failed'))) return;
      } catch { /* keep trying */ }
      await sleep(delay);
      delay = Math.min(delay * 1.25, 10000);
    }
  }

  // After slicing, make sure every multicolor slot the server found has a color saved.
  async syncColors() {
    const s = this.state;
    if (s.mode !== 'multi' || !s.quote) return;
    for (const it of s.quote.items || []) {
      if (!it.slice || it.slice.status !== 'done' || !Array.isArray(it.slice.filaments)) continue;
      const part = s.parts[it.n - 1];
      if (!part) continue;
      let changed = false;
      for (const f of it.slice.filaments) {
        if (!part.colors[f.slot]) {
          const pla = this.colorsFor('PLA');
          const used = new Set(Object.values(part.colors).map((c) => c.name));
          const pick = [...pla].sort((a, b) => colorDist(a.hex, f.placeholder) - colorDist(b.hex, f.placeholder)).find((c) => !used.has(c.name)) || pla[0];
          if (pick) { part.colors[f.slot] = pick; changed = true; }
        }
        if (!part.slots.includes(f.slot)) { part.slots = [...part.slots, f.slot].sort((a, b) => a - b); }
      }
      const saved = JSON.stringify((it.colors || []).map((c) => [c.slot, c.name]));
      const want = JSON.stringify(this.colorsPayload(part).map((c) => [c.slot, c.name]));
      if (changed || saved !== want) await this.saveColors(part, it.n);
    }
  }

  async saveColors(part, n) {
    const colors = this.colorsPayload(part);
    try {
      await this.call(`/quotes/${encodeURIComponent(this.state.quoteId)}/items/${n}`, { method: 'POST', body: { colors } });
      const it = this.state.quote && (this.state.quote.items || []).find((x) => x.n === n);
      if (it) it.colors = colors;
    } catch { /* retried on next poll */ }
  }

  /* ---------- events ---------- */

  onClick(e) {
    const t = e.target.closest('[data-act]');
    if (!t || !this.root.contains(t)) return;
    const act = t.dataset.act;
    const part = t.dataset.part ? this.state.parts.find((p) => p.id === t.dataset.part) : this.active;
    switch (act) {
      case 'mode': return this.setMode(t.dataset.mode);
      case 'change-type': this.state.phase = 'type'; return this.render();
      case 'pick-files': return this.root.querySelector('[data-file-input]').click();
      case 'activate': this.state.activeId = t.dataset.part; return this.render();
      case 'remove': return this.removePart(t.dataset.part);
      case 'slot-color': {
        const c = this.palette.find((x) => x.name === t.dataset.color && String(x.material || 'PLA').toUpperCase() === 'PLA');
        if (part && c) {
          part.colors[Number(t.dataset.slot)] = c;
          this.render();
          if (this.state.phase === 'done') this.debounceSave(part);
        }
        return;
      }
      case 'single-color': {
        if (!part) return;
        part.color = t.dataset.color === '' ? null : this.colorsFor(part.material).find((x) => x.name === t.dataset.color) || null;
        return this.render();
      }
      case 'surprise': {
        if (!part) return;
        const pla = [...this.colorsFor('PLA')].sort(() => Math.random() - 0.5);
        part.slots.forEach((slot, i) => { if (pla[i % pla.length]) part.colors[slot] = pla[i % pla.length]; });
        this.render();
        if (this.state.phase === 'done') this.debounceSave(part);
        return;
      }
      case 'file-colors': {
        if (!part) return;
        part.colors = this.defaultSlotColors(part);
        this.render();
        if (this.state.phase === 'done') this.debounceSave(part);
        return;
      }
      case 'reset-view': return this.viewer && this.viewer.reset();
      case 'toggle-advanced': if (part) { part.advancedOpen = !part.advancedOpen; this.render(); } return;
      case 'submit': return this.submit();
      case 'retry': return this.start();
      case 'new-quote': {
        Object.assign(this.state, { phase: 'type', mode: null, parts: [], activeId: null, quoteId: null, quote: null, notes: '', submitError: '' });
        try { history.replaceState(null, '', location.pathname); } catch { /* ignore */ }
        return this.render();
      }
    }
  }

  debounceSave(part) {
    clearTimeout(part._saveT);
    const n = this.state.parts.indexOf(part) + 1;
    part._saveT = setTimeout(() => this.saveColors(part, n), 600);
  }

  onChange(e) {
    const t = e.target;
    if (t.matches('[data-file-input]')) {
      this.addFiles(t.files);
      t.value = '';
      return;
    }
    const f = t.dataset.field;
    if (!f) return;
    const part = t.dataset.part ? this.state.parts.find((p) => p.id === t.dataset.part) : null;
    if (part) {
      if (f === 'material') {
        part.material = t.value;
        part.color = this.colorsFor(part.material)[0] || null;
        if (!this.supportChoices(part).some((c) => c.value === part.supportMaterial)) part.supportMaterial = '';
      } else if (f === 'support') {
        part.supportMaterial = t.value;
      } else if (f === 'units') {
        part.units = t.value;
        if (part.rawModel) part.model = part.units === 'in' ? this.M3.scaleModel(part.rawModel, 25.4) : part.rawModel;
        this.viewerModelId = null;
      } else if (f === 'qty') {
        part.qty = Math.max(1, Math.min(999, parseInt(t.value, 10) || 1));
      } else if (f.startsWith('adv:')) {
        const key = f.slice(4);
        const def = this.advancedDefs().find((d) => d.key === key);
        let v = t.type === 'checkbox' ? t.checked : t.value;
        if (def && def.type === 'number' && v !== '') v = Number(v);
        if (v === '' || (def && v === def.default)) delete part.settings[key];
        else part.settings[key] = v;
      }
      return this.render();
    }
    if (f === 'delivery') { this.state.delivery = t.value; return this.render(); }
    if (f === 'agreed') { this.state.agreed = t.checked; return this.renderSummary(); }
    if (f === 'astyped') { this.state.addressAsTyped = t.checked; return this.renderSummary(); }
    if (f.startsWith('addr:')) {
      this.state.address[f.slice(5)] = t.value;
      Object.assign(this.state, { addressNotFound: false, addressAsTyped: false });
      return this.render();
    }
  }

  onInput(e) {
    const t = e.target;
    const f = t.dataset.field;
    if (!f) return;
    if (f === 'notes') this.state.notes = t.value;
    else if (f === 'part-notes') {
      const part = this.state.parts.find((p) => p.id === t.dataset.part);
      if (part) part.notes = t.value;
    } else if (f.startsWith('addr:')) {
      this.state.address[f.slice(5)] = t.value;
      this.renderSummary();
    }
  }

  /* ---------- advanced settings (driven by /me options.advanced) ---------- */

  advancedDefs() {
    const raw = this.options.advanced;
    const list = Array.isArray(raw) ? raw : Object.entries(raw || {}).map(([key, d]) => ({ key, ...(typeof d === 'object' ? d : {}) }));
    return list
      .filter((d) => d && d.key)
      .map((d) => {
        const choices = d.options || d.choices || d.values;
        const type = d.type || (Array.isArray(choices) ? 'select' : typeof d.default === 'boolean' ? 'checkbox' : typeof d.default === 'number' || d.min != null ? 'number' : 'text');
        return { ...d, type, choices: Array.isArray(choices) ? choices : null, label: d.label || d.title || d.key };
      });
  }

  supportChoices(part) {
    const m = String(part.material).toUpperCase();
    const out = [{ value: '', label: `Standard (${m === 'RECOMMEND' ? 'same material' : m})`, hint: 'Supports print in the same material.' }];
    const mats = this.options.materials.map((x) => String(x).toUpperCase());
    if (m === 'PLA' && mats.includes('PETG')) {
      out.push({ value: 'PETG', label: 'PETG interface', hint: 'Where supports touch your part they print in PETG, which doesn’t bond to PLA. They peel off clean and leave a smoother underside.' });
    } else if (m === 'PETG' && mats.includes('PLA')) {
      out.push({ value: 'PLA', label: 'PLA interface', hint: 'Where supports touch your part they print in PLA, which doesn’t bond to PETG. They peel off clean and leave a smoother underside.' });
    }
    return out;
  }

  /* ---------- rendering ---------- */

  render() {
    const s = this.state;
    const T = this.cfg.text || {};
    let html = '';
    if (s.phase === 'loading') html = `<div class="qb-card qb-center"><div class="qb-spinner"></div><p>Loading…</p></div>`;
    else if (s.phase === 'login') html = this.viewLogin(T);
    else if (s.phase === 'error') html = `<div class="qb-card qb-center"><p>${esc(s.submitError || 'Could not reach the quote system.')}</p><button class="qb-btn" data-act="retry">Try again</button></div>`;
    else if (s.phase === 'type') html = this.viewType(T);
    else if (s.phase === 'build' || s.phase === 'submitting') html = this.viewBuild(T);
    else if (s.phase === 'done') html = this.viewDone(T);

    const body = this.root.querySelector('[data-qb-body]');
    const keep = this.root.querySelector('[data-qb-stage]');
    const scrollY = window.scrollY;
    const focused = document.activeElement && this.root.contains(document.activeElement) ? document.activeElement : null;
    const focusKey = focused && (focused.dataset.field ? `${focused.dataset.field}|${focused.dataset.part || ''}` : null);
    const caret = focused && 'selectionStart' in focused ? [focused.selectionStart, focused.selectionEnd] : null;

    // Keep the WebGL canvas alive across renders.
    if (keep) keep.remove();
    body.innerHTML = html;
    const slot = body.querySelector('[data-qb-stage-slot]');
    if (slot) {
      if (keep) slot.replaceWith(keep);
      else slot.replaceWith(this.createStage());
      this.updateStage();
    }
    if (focusKey) {
      const [field, part] = focusKey.split('|');
      const el = body.querySelector(`[data-field="${CSS.escape(field)}"]${part ? `[data-part="${CSS.escape(part)}"]` : ''}`);
      if (el) { el.focus({ preventScroll: true }); if (caret && 'setSelectionRange' in el) try { el.setSelectionRange(caret[0], caret[1]); } catch { /* */ } }
    }
    window.scrollTo(0, scrollY);
    const input = this.root.querySelector('[data-file-input]');
    if (input) input.accept = this.acceptFor(s.mode).map((t) => '.' + t).join(',');
    this.bindDrop();
  }

  bindDrop() {
    const zone = this.root.querySelector('[data-dropzone]');
    if (!zone || zone._bound) return;
    zone._bound = true;
    const on = (ev, fn) => zone.addEventListener(ev, fn);
    on('dragover', (e) => { e.preventDefault(); zone.classList.add('is-over'); });
    on('dragleave', () => zone.classList.remove('is-over'));
    on('drop', (e) => {
      e.preventDefault();
      zone.classList.remove('is-over');
      if (e.dataTransfer && e.dataTransfer.files.length) this.addFiles(e.dataTransfer.files);
    });
  }

  viewLogin(T) {
    const ret = encodeURIComponent(location.pathname + location.search);
    return `<div class="qb-card qb-center qb-login">
      <h3>${esc(T.loginTitle || 'Log in to get a quote')}</h3>
      <p>${esc(T.loginBody || 'Your account keeps your files, quotes and messages in one place. It only takes a minute.')}</p>
      <div class="qb-row qb-row-center">
        <a class="qb-btn" href="${esc(this.cfg.loginUrl || '/customer_authentication/login')}?return_to=${ret}">Sign in or create account</a>
      </div>
      <p class="qb-small qb-muted">No password needed: Shopify emails you a one-time sign-in code, and new emails get an account automatically.</p></div>`;
  }

  viewType(T) {
    const max = this.options.multicolor.maxSlots;
    const pla = this.colorsFor('PLA').slice(0, 4);
    const dots = (pla.length ? pla : [{ hex: '#e53935' }, { hex: '#1e88e5' }, { hex: '#fdd835' }, { hex: '#ffffff' }])
      .map((c) => `<span style="background:${esc(c.hex)}"></span>`).join('');
    const mats = this.options.materials.filter((m) => m !== 'Recommend').join(', ');
    return `<div class="qb-type">
      <h3 class="qb-h">${esc(T.typeTitle || 'What kind of print is this?')}</h3>
      <div class="qb-type-grid">
        <button class="qb-type-card ${this.state.mode === 'single' ? 'is-on' : ''}" data-act="mode" data-mode="single">
          <span class="qb-type-art qb-art-single"><span></span></span>
          <span class="qb-type-name">Single color</span>
          <span class="qb-type-desc">One material, one color per part.</span>
          <span class="qb-type-list">
            <span>STL or 3MF</span><span>${esc(mats)}</span><span>Optional PETG support interface on PLA parts</span>
          </span>
        </button>
        <button class="qb-type-card ${this.state.mode === 'multi' ? 'is-on' : ''}" data-act="mode" data-mode="multi">
          <span class="qb-type-art qb-art-multi">${dots}</span>
          <span class="qb-type-name">Multicolor</span>
          <span class="qb-type-desc">Up to ${max} colors in one part, painted or assigned in your slicer.</span>
          <span class="qb-type-list">
            <span>3MF only</span><span>PLA only</span><span>Preview and try colors from our stock</span>
          </span>
        </button>
      </div>
      ${this.state.parts.length ? `<p class="qb-muted qb-center-text"><button class="qb-link" data-act="mode" data-mode="${this.state.mode}">Back to my parts</button></p>` : ''}
    </div>`;
  }

  viewBuild(T) {
    const s = this.state;
    const multi = s.mode === 'multi';
    const busy = s.phase === 'submitting';
    const accept = this.acceptFor(s.mode).map((t) => t.toUpperCase()).join(' or ');
    return `<div class="qb-build ${busy ? 'is-busy' : ''}">
      <div class="qb-bar">
        <span class="qb-pill ${multi ? 'qb-pill-multi' : ''}">${multi ? 'Multicolor · PLA · 3MF' : 'Single color · STL or 3MF'}</span>
        ${busy ? '' : `<button class="qb-link" data-act="change-type">Change print type</button>`}
      </div>
      <div class="qb-grid">
        <div class="qb-stage-col">
          <div data-qb-stage-slot></div>
          ${this.viewColorPanel()}
        </div>
        <div class="qb-side">
          <div class="qb-drop" data-dropzone>
            <input type="file" multiple hidden data-file-input>
            <button class="qb-drop-btn" data-act="pick-files" ${busy ? 'disabled' : ''}>
              <strong>${s.parts.length ? 'Add another part' : 'Choose files'}</strong>
              <span>or drop ${accept} here · up to ${this.options.maxUploadMb} MB each</span>
            </button>
          </div>
          <div class="qb-parts">${s.parts.map((p) => this.viewPart(p)).join('')}</div>
          ${s.parts.length ? this.viewDelivery() : ''}
          ${s.parts.length ? `<label class="qb-field"><span>Anything else we should know?</span>
            <textarea data-field="notes" rows="2" placeholder="Deadline, fit or finish requirements, how the part is used…">${esc(s.notes)}</textarea></label>` : ''}
          <div data-qb-summary>${this.viewSummary()}</div>
        </div>
      </div>
    </div>`;
  }

  viewPart(p) {
    const s = this.state;
    const multi = s.mode === 'multi';
    const busy = s.phase === 'submitting';
    const on = p.id === s.activeId;
    const status = p.error
      ? `<p class="qb-err">${esc(p.error)}</p>`
      : p.parsing ? `<p class="qb-muted"><span class="qb-spinner qb-spinner-sm"></span> Reading file…</p>`
      : p.parseError ? `<p class="qb-note">${esc(p.parseError)}</p>`
      : p.note ? `<p class="qb-note">${esc(p.note)}</p>`
      : multi && p.model ? `<p class="qb-muted">${p.slots.length} colors found.</p>`
      : '';
    const upload = p.upload && busy
      ? `<div class="qb-progress"><span style="width:${Math.round((p.upload.progress || 0) * 100)}%"></span></div>` : '';

    let controls = '';
    if (!p.error) {
      const qty = `<label class="qb-field qb-field-qty"><span>Qty</span>
        <input type="number" min="1" max="999" value="${p.qty}" data-field="qty" data-part="${p.id}" ${busy ? 'disabled' : ''}></label>`;
      if (multi) {
        const chips = p.slots.map((slot) => {
          const c = p.colors[slot];
          return `<span class="qb-chip" title="Color ${slot}: ${esc(c ? c.name : 'not chosen')}"><i style="background:${esc(c ? c.hex : 'transparent')}"></i>${esc(c ? c.name : '—')}</span>`;
        }).join('');
        controls = `<div class="qb-row">${qty}<label class="qb-field"><span>Material</span><select disabled><option>PLA (multicolor)</option></select></label></div>
          ${chips ? `<div class="qb-chips">${chips}</div>` : ''}`;
      } else {
        const mats = this.options.materials.map((m) =>
          `<option value="${esc(m)}" ${m === p.material ? 'selected' : ''}>${m === 'Recommend' ? 'Not sure — recommend one' : esc(m)}</option>`).join('');
        const units = p.ext === 'stl'
          ? `<label class="qb-field"><span>File units</span><select data-field="units" data-part="${p.id}" ${busy ? 'disabled' : ''}>
              <option value="mm" ${p.units === 'mm' ? 'selected' : ''}>Millimeters</option>
              <option value="in" ${p.units === 'in' ? 'selected' : ''}>Inches</option></select></label>` : '';
        const supports = this.supportChoices(p);
        const supportHtml = supports.length > 1 ? `<fieldset class="qb-field qb-support"><legend>Supports</legend>
            <div class="qb-seg">${supports.map((c) => `<label class="${p.supportMaterial === c.value ? 'is-on' : ''}">
              <input type="radio" name="sup-${p.id}" value="${esc(c.value)}" data-field="support" data-part="${p.id}" ${p.supportMaterial === c.value ? 'checked' : ''} ${busy ? 'disabled' : ''}>${esc(c.label)}</label>`).join('')}</div>
            <small class="qb-muted">${esc((supports.find((c) => c.value === p.supportMaterial) || supports[0]).hint)} Only used if the part needs supports.</small>
          </fieldset>` : '';
        const colors = this.colorsFor(p.material);
        const sw = colors.map((c) => `<button class="qb-sw ${p.color && p.color.name === c.name ? 'is-on' : ''}" style="--sw:${esc(c.hex)};--ink:${inkOn(c.hex)}"
            data-act="single-color" data-part="${p.id}" data-color="${esc(c.name)}" title="${esc(c.name)}" aria-label="${esc(c.name)}" ${busy ? 'disabled' : ''}></button>`).join('');
        const colorHtml = `<div class="qb-field"><span>Color · <b>${esc(p.color ? p.color.name : 'Your pick')}</b></span>
          <div class="qb-swatches">${sw}<button class="qb-sw qb-sw-any ${!p.color ? 'is-on' : ''}" data-act="single-color" data-part="${p.id}" data-color="" title="Any color — you pick" ${busy ? 'disabled' : ''}>?</button></div></div>`;
        controls = `<div class="qb-row">${qty}<label class="qb-field"><span>Material</span>
            <select data-field="material" data-part="${p.id}" ${busy ? 'disabled' : ''}>${mats}</select></label>${units}</div>
          ${colorHtml}${supportHtml}`;
      }
      const defs = this.advancedDefs();
      if (defs.length) {
        controls += `<button class="qb-link qb-adv-toggle" data-act="toggle-advanced" data-part="${p.id}">${p.advancedOpen ? 'Hide' : 'Show'} advanced print settings${Object.keys(p.settings).length ? ` (${Object.keys(p.settings).length} changed)` : ''}</button>`;
        if (p.advancedOpen) controls += `<div class="qb-adv">${defs.map((d) => this.viewAdvField(p, d, busy)).join('')}</div>`;
      }
      controls += `<label class="qb-field"><span>Notes for this part</span>
        <input type="text" value="${esc(p.notes)}" data-field="part-notes" data-part="${p.id}" placeholder="Optional" ${busy ? 'disabled' : ''}></label>`;
    }

    return `<article class="qb-part ${on ? 'is-active' : ''} ${p.error ? 'has-error' : ''}">
      <header>
        <button class="qb-part-name" data-act="activate" data-part="${p.id}" title="Show in preview">
          <span class="qb-ext">${esc(p.ext.toUpperCase())}</span><span>${esc(p.name)}</span>
        </button>
        <span class="qb-muted qb-size">${mb(p.size)}</span>
        ${busy ? '' : `<button class="qb-x" data-act="remove" data-part="${p.id}" aria-label="Remove ${esc(p.name)}">×</button>`}
      </header>
      ${upload}${status}${controls}
    </article>`;
  }

  viewAdvField(p, d, busy) {
    const val = p.settings[d.key] ?? d.default ?? '';
    const attrs = `data-field="adv:${esc(d.key)}" data-part="${p.id}" ${busy ? 'disabled' : ''}`;
    let input;
    if (d.type === 'select' && d.choices) {
      input = `<select ${attrs}>${d.choices.map((c) => {
        const v = typeof c === 'object' ? c.value : c, l = typeof c === 'object' ? c.label || c.value : c;
        return `<option value="${esc(v)}" ${String(v) === String(val) ? 'selected' : ''}>${esc(l)}</option>`;
      }).join('')}</select>`;
    } else if (d.type === 'checkbox' || d.type === 'boolean') {
      input = `<input type="checkbox" ${attrs} ${val ? 'checked' : ''}>`;
    } else if (d.type === 'number') {
      input = `<input type="number" ${attrs} value="${esc(val)}" ${d.min != null ? `min="${d.min}"` : ''} ${d.max != null ? `max="${d.max}"` : ''} ${d.step != null ? `step="${d.step}"` : ''}>`;
    } else {
      input = `<input type="text" ${attrs} value="${esc(val)}">`;
    }
    return `<label class="qb-field ${d.type === 'checkbox' ? 'qb-field-check' : ''}"><span>${esc(d.label)}${d.unit ? ` (${esc(d.unit)})` : ''}</span>${input}</label>`;
  }

  viewColorPanel() {
    const p = this.active;
    if (!p || this.state.mode !== 'multi' || !p.slots.length || p.error) return '';
    const pla = this.colorsFor('PLA');
    if (!pla.length) return `<div class="qb-card qb-note">No PLA colors are marked in stock right now.</div>`;
    const busy = this.state.phase === 'submitting';
    const rows = p.slots.map((slot) => {
      const cur = p.colors[slot];
      const sw = pla.map((c) => `<button class="qb-sw ${cur && cur.name === c.name ? 'is-on' : ''}" style="--sw:${esc(c.hex)};--ink:${inkOn(c.hex)}"
          data-act="slot-color" data-part="${p.id}" data-slot="${slot}" data-color="${esc(c.name)}" title="${esc(c.name)}" aria-label="Color ${slot}: ${esc(c.name)}" ${busy ? 'disabled' : ''}></button>`).join('');
      return `<div class="qb-slot"><div class="qb-slot-head"><span class="qb-slot-num">${slot}</span><b>${esc(cur ? cur.name : 'Choose a color')}</b></div>
        <div class="qb-swatches">${sw}</div></div>`;
    }).join('');
    return `<div class="qb-card qb-colors">
      <div class="qb-colors-head"><h4>Colors for ${esc(p.name)}</h4>
        <div class="qb-row qb-gap-sm">
          <button class="qb-link" data-act="surprise" data-part="${p.id}" ${busy ? 'disabled' : ''}>Surprise me</button>
          ${p.model && Object.keys(p.model.fileColors || {}).length ? `<button class="qb-link" data-act="file-colors" data-part="${p.id}" ${busy ? 'disabled' : ''}>Match my file</button>` : ''}
        </div></div>
      ${rows}
      <p class="qb-muted qb-small">In-stock PLA only. Changing colors never changes the price.</p>
    </div>`;
  }

  viewDelivery() {
    const s = this.state;
    const d = this.options.deliveries;
    const R = this.P.RATES.delivery;
    const radios = Object.entries(d).map(([key, v]) => {
      const fee = key === 'ship' ? 'USPS rate added after review' : R[key] ? this.P.money(R[key]) : 'Free';
      return `<label class="qb-radio ${s.delivery === key ? 'is-on' : ''}"><input type="radio" name="qb-delivery" value="${esc(key)}" data-field="delivery" ${s.delivery === key ? 'checked' : ''}>
        <span><b>${esc(v.title || key)}</b><small>${esc(fee)}</small></span></label>`;
    }).join('');
    let addr = '';
    if (this.needsAddress()) {
      const a = s.address;
      const states = this.options.states.length ? this.options.states : ['TX'];
      const stateOpts = states.map((st) => {
        const code = typeof st === 'object' ? st.code || st.value : st;
        const name = typeof st === 'object' ? st.name || st.label || code : st;
        return `<option value="${esc(code)}" ${code === a.state ? 'selected' : ''}>${esc(name)}</option>`;
      }).join('');
      const f = (k, label, ph = '', cls = '') => `<label class="qb-field ${cls}"><span>${label}</span><input type="text" value="${esc(a[k] || '')}" data-field="addr:${k}" placeholder="${esc(ph)}" autocomplete="${{ name: 'name', address1: 'address-line1', address2: 'address-line2', city: 'address-level2', zip: 'postal-code' }[k] || 'on'}"></label>`;
      addr = `<div class="qb-addr">
        ${s.delivery === 'local' ? `<p class="qb-muted qb-small">Local delivery is within ${this.options.localRadius} miles of Lubbock.</p>` : ''}
        ${f('name', 'Name')}${f('address1', 'Street address')}${f('address2', 'Apt, suite (optional)')}
        <div class="qb-row">${f('city', 'City', '', 'qb-grow')}<label class="qb-field"><span>State</span><select data-field="addr:state">${stateOpts}</select></label>${f('zip', 'ZIP', '', 'qb-field-zip')}</div>
        ${s.addressNotFound ? `<label class="qb-check"><input type="checkbox" data-field="astyped" ${s.addressAsTyped ? 'checked' : ''}><span>We couldn’t find this address. It’s correct, use it as typed.</span></label>` : ''}
      </div>`;
    }
    return `<fieldset class="qb-card qb-delivery"><legend>Delivery</legend><div class="qb-radios">${radios}</div>${addr}</fieldset>`;
  }

  viewSummary() {
    const s = this.state;
    if (!s.parts.length) {
      return `<div class="qb-card qb-howto">${this.viewRates()}</div>`;
    }
    const problems = this.problems();
    const busy = s.phase === 'submitting';
    return `<div class="qb-card qb-summary">
      ${this.viewRates()}
      <p class="qb-small qb-muted">Submit and we slice every part right away — your estimate shows up here in a minute or two. We review every quote before sending an invoice.</p>
      <label class="qb-check"><input type="checkbox" data-field="agreed" ${s.agreed ? 'checked' : ''} ${busy ? 'disabled' : ''}><span>I have the right to have these files printed, and I agree to the <a href="${esc(this.cfg.termsUrl || '/pages/terms-of-service')}" target="_blank" rel="noopener">Terms of Service</a>.</span></label>
      ${s.submitError ? `<p class="qb-err">${esc(s.submitError)}</p>` : ''}
      ${problems.length && !busy ? `<ul class="qb-problems">${problems.map((x) => `<li>${esc(x)}</li>`).join('')}</ul>` : ''}
      <button class="qb-btn qb-btn-wide" data-act="submit" ${problems.length || busy ? 'disabled' : ''}>
        ${busy ? '<span class="qb-spinner qb-spinner-sm"></span> Uploading…' : `Get my quote · ${s.parts.length} part${s.parts.length > 1 ? 's' : ''}`}
      </button>
    </div>`;
  }

  viewRates() {
    const R = this.P.RATES;
    const multi = this.state.mode === 'multi';
    const cents = (v) => (v * 100).toFixed(1).replace(/\.0$/, '') + '¢';
    return `<dl class="qb-rates">
      <div><dt>Print time</dt><dd>${this.P.money(R.hourly)} / hr, including printer setup (bed leveling and calibration)</dd></div>
      <div><dt>Filament</dt><dd>at cost · PLA, PETG, ABS ${cents(R.perGram.PLA)}/g · TPU ${cents(R.perGram.TPU)}/g</dd></div>
      ${multi ? `<div><dt>Multicolor</dt><dd>color-change time billed as print time, purge filament at cost</dd></div>` : ''}
      <div><dt>Minimum</dt><dd>${this.P.money(R.minimumOrder)} per order</dd></div>
    </dl>`;
  }

  renderSummary() {
    const el = this.root.querySelector('[data-qb-summary]');
    if (el) el.innerHTML = this.viewSummary();
  }

  renderProgress() {
    for (const p of this.state.parts) {
      if (!p.upload) continue;
      const bar = this.root.querySelector(`.qb-part [data-act="activate"][data-part="${p.id}"]`);
      const span = bar && bar.closest('.qb-part').querySelector('.qb-progress span');
      if (span) span.style.width = Math.round((p.upload.progress || 0) * 100) + '%';
    }
  }

  viewDone() {
    const s = this.state;
    const q = s.quote;
    const P = this.P;
    const items = (q && q.items) || [];
    const priced = q ? P.priceQuote({ ...q, delivery: q.delivery || s.delivery }) : null;
    const quoteUrl = `${this.cfg.quotesPage || '/pages/quotes'}?q=${encodeURIComponent(s.quoteId)}`;
    const statusLabel = { queued: 'Waiting to slice', slicing: 'Slicing…', done: 'Sliced', failed: 'Couldn’t slice' };

    const rows = (items.length ? items : s.parts.map((p, i) => ({ n: i + 1, name: p.name, qty: p.qty, slice: { status: 'queued' } })))
      .map((it) => {
        const part = s.parts[it.n - 1];
        const st = (it.slice && it.slice.status) || 'queued';
        const p = priced && priced.items ? priced.items.find((x) => x.n === it.n) : null;
        const swatches = s.mode === 'multi' && part
          ? `<div class="qb-chips">${part.slots.map((slot) => {
              const c = part.colors[slot];
              return `<span class="qb-chip"><i style="background:${esc(c ? c.hex : 'transparent')}"></i>${esc(c ? c.name : '—')}</span>`;
            }).join('')}</div>` : '';
        const dims = it.slice && Array.isArray(it.slice.dims) ? it.slice.dims.map((d) => Math.round(d)).join(' × ') + ' mm' : '';
        return `<article class="qb-part qb-result ${part && part.id === s.activeId ? 'is-active' : ''}">
          <header>
            <button class="qb-part-name" ${part ? `data-act="activate" data-part="${part.id}"` : ''}><span class="qb-ext">${esc(extOf(it.name).toUpperCase())}</span><span>${esc(it.name)}</span></button>
            <span class="qb-price">${p && p.amount != null ? P.money(p.amount) : ''}</span>
          </header>
          <p class="qb-status qb-st-${st}">${st === 'slicing' || st === 'queued' ? '<span class="qb-spinner qb-spinner-sm"></span> ' : ''}${esc(statusLabel[st] || st)}${it.qty > 1 ? ` · ${it.qty} copies` : ''}${dims ? ` · ${dims}` : ''}</p>
          ${st === 'done' ? `<p class="qb-small">${esc(P.breakdownText(it))} <span class="qb-muted">(per copy)</span></p>` : ''}
          ${st === 'done' && it.slice.fits === false ? `<p class="qb-note">This part is bigger than our build plate. We’ll reach out about splitting it.</p>` : ''}
          ${st === 'failed' ? `<p class="qb-err">${esc((it.slice && it.slice.error) || 'We’ll take a look and price it by hand.')}</p>` : ''}
          ${it.override != null ? `<p class="qb-note">Priced by hand.</p>` : ''}
          ${swatches}
        </article>`;
      }).join('');

    let totals = '';
    if (priced) {
      const line = (label, val, cls = '') => `<div class="${cls}"><dt>${esc(label)}</dt><dd>${val}</dd></div>`;
      const extras = (priced.extras || []).map((e) => line(e.title, P.money(e.amount))).join('');
      const lines = (priced.lines || []).map((l) => line(l.title, P.money(l.amount))).join('');
      const deliveryTitle = (this.options.deliveries[s.delivery] || {}).title || 'Delivery';
      totals = `<dl class="qb-totals">
        ${extras}${lines}
        ${priced.discount ? line(priced.discountReason || 'Discount', '−' + P.money(priced.discount)) : ''}
        ${line(deliveryTitle, priced.shippingTBD ? 'Shipping TBD' : priced.delivery ? P.money(priced.delivery) : 'Free')}
        ${line('Estimated total', priced.pending ? 'Calculating…' : priced.total == null ? `${P.money(priced.subtotal)} + shipping` : P.money(priced.total), 'qb-total')}
      </dl>`;
    }

    const allDone = items.length && items.every((it) => it.slice && (it.slice.status === 'done' || it.slice.status === 'failed'));
    return `<div class="qb-build">
      <div class="qb-bar"><span class="qb-pill qb-pill-ok">Quote ${esc(s.quoteId)} submitted</span>
        <button class="qb-link" data-act="new-quote">Start another quote</button></div>
      <div class="qb-grid">
        <div class="qb-stage-col"><div data-qb-stage-slot></div>${this.viewColorPanel()}</div>
        <div class="qb-side">
          <div class="qb-parts">${rows}</div>
          <div class="qb-card qb-summary">
            ${totals}
            <p class="qb-small qb-muted">${allDone
              ? 'This is an automatic estimate. We’ll review it and send your invoice — you’ll get an email when it’s ready.'
              : 'Slicing usually takes a minute or two per part. You can leave this page; we’ll email you when your quote is ready.'}</p>
            <a class="qb-btn qb-btn-wide" href="${esc(quoteUrl)}">Open quote &amp; messages</a>
          </div>
        </div>
      </div>
    </div>`;
  }

  /* ---------- 3D stage ---------- */

  createStage() {
    const stage = document.createElement('div');
    stage.className = 'qb-stage';
    stage.setAttribute('data-qb-stage', '');
    stage.innerHTML = `<canvas class="qb-canvas" aria-label="3D preview"></canvas>
      <div class="qb-stage-msg" data-stage-msg></div>
      <div class="qb-stage-tools"><button class="qb-icon" data-act="reset-view" title="Reset view" aria-label="Reset view">⟲</button></div>
      <div class="qb-stage-hint">Drag to rotate · scroll or pinch to zoom · right-drag to pan</div>`;
    const canvas = stage.querySelector('canvas');
    this.viewer = this.V.createViewer(canvas, { gridColor: this.resolveColor('var(--qb-grid)', '#c4c7cc') });
    this.viewerModelId = null;
    return stage;
  }

  // Turn a CSS color (incl. color-mix / custom properties) into #rrggbb.
  resolveColor(css, fallback) {
    try {
      const el = document.createElement('span');
      el.style.color = css;
      this.root.appendChild(el);
      const c = getComputedStyle(el).color;
      el.remove();
      let m = c.match(/rgba?\(\s*([\d.]+)[ ,]+([\d.]+)[ ,]+([\d.]+)/);
      let v = m ? [m[1], m[2], m[3]].map(Number) : null;
      if (!v) { m = c.match(/color\(srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)/); if (m) v = [m[1], m[2], m[3]].map((x) => Number(x) * 255); }
      if (!v) return fallback;
      return '#' + v.map((x) => Math.round(x).toString(16).padStart(2, '0')).join('');
    } catch { return fallback; }
  }

  updateStage() {
    const stage = this.root.querySelector('[data-qb-stage]');
    if (!stage) return;
    const msg = stage.querySelector('[data-stage-msg]');
    const p = this.active;
    let text = '';
    if (!this.viewer) text = 'Your browser can’t show 3D previews, but you can still get a quote.';
    else if (!p) text = this.state.mode === 'multi' ? 'Add a 3MF to preview it and try colors.' : 'Add a part to preview it.';
    else if (p.error) text = p.error;
    else if (p.parsing) text = 'Reading file…';
    else if (!p.model) text = p.parseError || 'No preview for this part.';
    msg.textContent = text;
    stage.classList.toggle('is-empty', !!text);
    if (!this.viewer) return;
    const model = p && !p.error ? p.model : null;
    const key = model ? p.id + ':' + p.units : null;
    if (key !== this.viewerModelId) {
      this.viewer.setModel(model);
      this.viewerModelId = key;
    }
    if (model) {
      for (const slot of model.slots) {
        let hex;
        if (this.state.mode === 'multi') hex = p.colors[slot] ? p.colors[slot].hex : (model.fileColors[slot] || '#cfcfcf');
        else hex = p.color ? p.color.hex : '#bdbdbd';
        this.viewer.setSlotColor(slot, hex);
      }
    }
  }
}
